import RefreshToken from '@containers/RefreshToken';
import React from 'react';
import { Switch } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

import AuthorizedPageLayout from '@app/layouts/AuthorizedPageLayout';
import UnauthorizedPageLayout from '@app/layouts/UnauthorizedPageLayout';
import AssetsProvider from '@app/providers/AssetsProvider';
import ResourcesProvider from '@app/providers/ResourcesProvider';
import MatchCurrentBranchRoute from '@app/routes/components/MatchCurrentBranchRoute';
import NotRetrievedRoute from '@app/routes/components/NotRetrievedRoute';
import OnlyAuthorizedRoute from '@app/routes/components/OnlyAuthorizedRoute';
import OnlyUnauthorizedRoute from '@app/routes/components/OnlyUnauthorizedRoute';
import RegistrationProtectedRoute from '@app/routes/components/RegistrationProtectedRoute';
import RoutePaths, { AUTHORIZED_ROUTES, UNAUTHORIZED_ROUTES, VERIFICATION_ROUTES } from '@app/routes/paths';
import useGenerateRoutePath from '@app/routes/paths/useGenerateRoutePath';

import { AnimatedRedirect, AnimatedRoute, AnimatedSwitch } from '@components/transition/route';

import useBranchEnvironment from '@hooks/useBranchEnvironment';
import useEnforceQueryParams from '@hooks/useEnforceQueryParams';

import ApplicationFormPage from '@pages/ApplicationForm';
import ApplicationsPage from '@pages/Applications';
import ForgotPasswordPage from '@pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from '@pages/Auth/ForgotPasswordPage/containers/ResetPassword';
import ResetPasswordMethod from '@pages/Auth/ForgotPasswordPage/containers/RessetPasswordMethod';
import LoginPage from '@pages/Auth/LoginPage';
import TermsAndConditionPage from '@pages/Auth/TermsAndConditionPage';
import UserExistsPage from '@pages/Auth/UserExists';
import AccountVerificationPage from '@pages/Verification/AccountVerificationPage';
import MFASetupPage from '@pages/Verification/MFASetupPage';
import MFAVerifyPage from '@pages/Verification/MFAVerifyPage';

import OnlyVerifiedAccountRoute from './components/OnlyVerifiedAccountRoute';

function AppRoutes() {
	useBranchEnvironment();
	useEnforceQueryParams();

	const generateRoutePath = useGenerateRoutePath();
	const route = useLocation().pathname;

	return (
		<ResourcesProvider>
			<Switch>
				<AnimatedRoute exact path={UNAUTHORIZED_ROUTES} animation={'fade'}>
					<OnlyUnauthorizedRoute>
						<UnauthorizedPageLayout fullPage={route.includes('terms-and-conditions')}>
							<AnimatedSwitch>
								<AnimatedRoute animation={'fade'} path={RoutePaths.AUTH.REGISTRATION()}>
									<RegistrationProtectedRoute />
								</AnimatedRoute>

								<AnimatedRoute animation={'fade'} path={RoutePaths.AUTH.LOGIN()}>
									<LoginPage />
								</AnimatedRoute>

								<AnimatedRoute animation={'fade'} path={RoutePaths.AUTH.USER_EXISTS()}>
									<UserExistsPage />
								</AnimatedRoute>

								<AnimatedRoute animation={'fade'} path={RoutePaths.AUTH.TERMSANDCONDITIONS()}>
									<TermsAndConditionPage />
								</AnimatedRoute>

								<AnimatedRoute animation={'fade'} path={RoutePaths.AUTH.FORGOT_PASSWORD_METHOD()}>
									<ForgotPasswordPage />
								</AnimatedRoute>

								<AnimatedRoute animation={'fade'} path={RoutePaths.AUTH.FORGOT_PASSWORD_VIA_EMAIL()}>
									<ResetPasswordMethod />
								</AnimatedRoute>

								<AnimatedRoute animation={'fade'} path={RoutePaths.AUTH.FORGOT_PASSWORD_VIA_PHONE()}>
									<ResetPasswordMethod />
								</AnimatedRoute>

								<AnimatedRoute animation={'fade'} path={RoutePaths.AUTH.RESET_PASSWORD()}>
									<ResetPasswordPage />
								</AnimatedRoute>

								<AnimatedRedirect to={generateRoutePath(RoutePaths.AUTH.LOGIN)} />
							</AnimatedSwitch>
						</UnauthorizedPageLayout>
					</OnlyUnauthorizedRoute>
				</AnimatedRoute>

				<AnimatedRoute exact path={RoutePaths.VERIFICATION.MFA_VERIFY()} animation={'fade'}>
					<OnlyUnauthorizedRoute>
						<UnauthorizedPageLayout isPostLogin={true}>
							<MFAVerifyPage />
						</UnauthorizedPageLayout>
					</OnlyUnauthorizedRoute>
				</AnimatedRoute>

				<AnimatedRoute animation={'fade'} exact path={VERIFICATION_ROUTES}>
					<OnlyVerifiedAccountRoute>
						<UnauthorizedPageLayout isPostLogin={true}>
							<AnimatedSwitch>
								<AnimatedRoute exact animation={'fade'} path={RoutePaths.VERIFICATION.ACCOUNT()}>
									<AccountVerificationPage />
								</AnimatedRoute>
								<AnimatedRoute exact animation={'fade'} path={RoutePaths.VERIFICATION.MFA_SETUP()}>
									<MFASetupPage />
								</AnimatedRoute>
							</AnimatedSwitch>
						</UnauthorizedPageLayout>
					</OnlyVerifiedAccountRoute>
				</AnimatedRoute>

				<AnimatedRoute animation={'fade'} exact path={AUTHORIZED_ROUTES}>
					<AssetsProvider>
						<OnlyAuthorizedRoute>
							<AuthorizedPageLayout>
								<RefreshToken />
								<AnimatedSwitch>
									<AnimatedRoute animation={'fade'} exact path={RoutePaths.MAIN.APPLICATIONS()}>
										<MatchCurrentBranchRoute>
											<ApplicationsPage />
										</MatchCurrentBranchRoute>
									</AnimatedRoute>

									<AnimatedRoute animation={'fade'} exact path={RoutePaths.MAIN.APPLICATION_FORM()}>
										<NotRetrievedRoute>
											<MatchCurrentBranchRoute>
												<ApplicationFormPage />
											</MatchCurrentBranchRoute>
										</NotRetrievedRoute>
									</AnimatedRoute>
								</AnimatedSwitch>
							</AuthorizedPageLayout>
						</OnlyAuthorizedRoute>
					</AssetsProvider>
				</AnimatedRoute>

				<AnimatedRedirect to={generateRoutePath(RoutePaths.AUTH.LOGIN)} />
			</Switch>
		</ResourcesProvider>
	);
}

export default AppRoutes;
